import React, { useState } from 'react';
import { Button, message, Modal, Upload } from 'antd';
import { CameraOutlined, UploadOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useReactFlow } from 'reactflow';
import { toPng } from 'html-to-image';
import { MindMapContext } from '../../Context/contex';
import { templateService } from '../../Api/api';
import './TemplateThumbnail.css';

const TemplateThumbnail = () => {
  const { documentId } = useParams();
  const { isTemplateDocument, templateData, setTemplateData, setSavingData } = MindMapContext();
  const { getViewport, setViewport, fitView } = useReactFlow();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [customUrl, setCustomUrl] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);

  const handleOpenModal = () => {
    setIsModalVisible(true);
    const thumb = templateData?.thumbnail;
    const isImage = (url) => {
      if (!url || typeof url !== 'string') return false;
      return url.startsWith('data:image/') || /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(url);
    };

    if (thumb && isImage(thumb)) {
      setPreviewUrl(thumb);
    } else {
      // Capture the current view immediately
      handleCapture();
    }
  };

  const handleCapture = async () => {
    setIsCapturing(true);
    // Save current viewport so we can restore it after capture
    const previousViewport = getViewport();
    try {
      const container = document.querySelector('.react-flow');
      if (!container) {
        message.error('Mind map container not found');
        return;
      }

      // Fit all nodes into the visible area with a small padding
      await fitView({ padding: 0.08, duration: 0 });

      // Wait one frame so the DOM reflects the new transform
      await new Promise((resolve) => requestAnimationFrame(resolve));
      const { width, height } = container.getBoundingClientRect();
      const TEMPLATE_WIDTH = 1280;
      const TEMPLATE_HEIGHT = 720;
      const scale = Math.min(TEMPLATE_WIDTH / width, TEMPLATE_HEIGHT / height);

      const dataUrl = await toPng(container, {
        backgroundColor: '#ffffff',
        width: Math.round(width * scale),
        height: Math.round(height * scale),
        style: {
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          width: `${width}px`,
          height: `${height}px`,
        },
        // skip UI overlay elements (controls, minimap, sidebar)
        filter: (node) => {
          const cls = node.classList;
          if (!cls) return true;
          return !cls.contains('react-flow__controls') &&
            !cls.contains('react-flow__minimap') &&
            !cls.contains('sideBarDragDropIcons') &&
            !cls.contains('mindMap-options');
        },
      });

      setPreviewUrl(dataUrl);
    } catch (error) {
      console.error('Capture error:', error);
      message.error('Failed to capture current view');
    } finally {
      // Restore the user's original pan/zoom
      setViewport(previousViewport, { duration: 0 });
      setIsCapturing(false);
    }
  };

  const handleConfirmUpdate = async () => {
    setIsSaving(true);
    try {
      const thumbnailUrl = customUrl || previewUrl;
      const res = await templateService.updateTemplate({ templateId: documentId, data: { thumbnail: thumbnailUrl }, dispatch: setSavingData });
      message.success('Thumbnail saved successfully');
      setIsModalVisible(false);
      setPreviewUrl(null);
      setCustomUrl(null);
      // Update templateData
      if (res?.data?.data) {
        setTemplateData(res.data.data);
      }
    } catch (error) {
      message.error('Failed to save thumbnail');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setPreviewUrl(null);
    setCustomUrl(null);
  };

  const handleUpload = (info) => {
    const file = info.file;
    if (file) {
      // Check file size (1 MB limit)
      const maxSize = 1 * 1024 * 1024; // 1 MB in bytes
      if (file.size > maxSize) {
        message.error('File size must be less than 1 MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setCustomUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  if (!isTemplateDocument) return null;

  return (
    <>
      <Button
        type="text"
        icon={<CameraOutlined className="thumbnail-button-icon" />}
        onClick={handleOpenModal}
        title="Save Thumbnail"
        size="small"
        className="thumbnail-button"
      />
      <Modal
        title={<div className="thumbnail-modal-header"><CameraOutlined className="thumbnail-modal-icon" />Thumbnail Preview</div>}
        open={isModalVisible}
        onCancel={handleCancel}
        className="thumbnail-modal"
        width={560}
        footer={[
          <Button key="cancel" onClick={handleCancel} className="cancel-btn">
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleConfirmUpdate} className="update-thumbnail-btn" loading={isSaving}>
            Save Thumbnail
          </Button>,
        ]}
      >
        <div className="thumbnail-content">
          <div className="thumbnail-preview-container" role="img" aria-label="thumbnail-preview">
            {(customUrl || previewUrl) ? (
              <img src={customUrl || previewUrl} alt="Thumbnail Preview" className="thumbnail-preview" />
            ) : (
              <div className="thumbnail-placeholder">
                <div className="placeholder-icon">📄</div>
                <p>No thumbnail yet</p>
              </div>
            )}
          </div>
          <div className="upload-section">
            <div className="upload-label">Upload a custom thumbnail</div>
            <Upload
              beforeUpload={() => false}
              onChange={handleUpload}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />} className="upload-btn">Upload Custom Thumbnail</Button>
            </Upload>
            <div className="capture-label">Capture current view</div>
            <Button
              icon={<CameraOutlined />}
              onClick={handleCapture}
              loading={isCapturing}
              className="capture-btn"
            >
              {isCapturing ? 'Capturing…' : 'Capture Current View'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TemplateThumbnail;